import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const ProductsDropdown: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    // Add a small delay before closing to make it easier to move to the dropdown
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      setHoveredItem(null);
    }, 100);
  };

  const handleRustClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/products/rust');
  };
  
  return (
    <div 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Create an invisible area that connects the nav item to the dropdown to prevent closing */}
      {isHovered && (
        <div 
          className="absolute w-full h-4 bottom-0 left-0 transform translate-y-full"
          onMouseEnter={handleMouseEnter}
        ></div>
      )}
      <div 
        className="text-sm font-medium bg-gradient-to-r from-blue-300 via-blue-400 to-blue-200 bg-clip-text text-transparent hover:opacity-80 transition-opacity py-1 flex items-center cursor-pointer"
      >
        Products
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          className={`ml-1 text-blue-400 transition-transform duration-300 ${isHovered ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      
      {isHovered && (
        <>
          {/* Improved triangle pointer */}
          <div 
            className="absolute left-1/2 transform -translate-x-1/2 top-full"
            style={{
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid rgba(17, 24, 39, 0.95)',
              marginTop: '4px',
              zIndex: 60,
              filter: 'drop-shadow(0 -1px 2px rgba(59, 130, 246, 0.3))'
            }}
          ></div>
          
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 w-56 overflow-hidden z-50 rounded-md"
            style={{
              background: 'linear-gradient(to bottom, rgba(17, 24, 39, 0.95), rgba(17, 24, 39, 0.98))',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5), 0 0 15px rgba(59, 130, 246, 0.3)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              animation: 'fadeIn 0.4s ease-out forwards'
            }}
            onMouseEnter={handleMouseEnter}
          >
            {/* Top gradient bar similar to pricing cards */}
            <div className="h-1 w-full bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"></div>
            
            <div className="py-2" onMouseEnter={handleMouseEnter}>
              <button
                className="flex items-center justify-between px-4 py-2 text-sm text-gray-100 transition-all duration-200 relative w-full text-left"
                style={{ 
                  position: 'relative',
                  backgroundColor: hoveredItem === 'rust' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  borderLeft: '2px solid',
                  borderLeftColor: hoveredItem === 'rust' ? '#60a5fa' : 'transparent',
                  paddingLeft: '1rem'
                }}
                onClick={handleRustClick}
                onMouseEnter={() => setHoveredItem('rust')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span 
                  className="font-medium transition-colors duration-200"
                  style={{ color: hoveredItem === 'rust' ? '#93c5fd' : '#f3f4f6' }}
                >Rust</span>
                <div className="relative flex items-center justify-center">
                  <span 
                    className="w-3 h-3 rounded-full bg-green-400 transition-shadow duration-200"
                    style={{ 
                      boxShadow: hoveredItem === 'rust' 
                        ? '0 0 12px 4px rgba(74, 222, 128, 0.7)' 
                        : '0 0 8px 2px rgba(74, 222, 128, 0.4)'
                    }}
                  ></span>
                  <span className="absolute w-3 h-3 rounded-full bg-green-400 animate-ping opacity-75" style={{animationDuration: '3s'}}></span>
                </div>
              </button>
              <button
                className="flex items-center justify-between px-4 py-2 text-sm text-gray-100 transition-all duration-200 relative w-full text-left"
                style={{ 
                  position: 'relative',
                  backgroundColor: hoveredItem === 'apex' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  borderLeft: '2px solid',
                  borderLeftColor: hoveredItem === 'apex' ? '#60a5fa' : 'transparent',
                  paddingLeft: '1rem'
                }}
                onClick={(e) => e.preventDefault()}
                onMouseEnter={() => setHoveredItem('apex')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span 
                  className="font-medium transition-colors duration-200"
                  style={{ color: hoveredItem === 'apex' ? '#93c5fd' : '#f3f4f6' }}
                >Apex Legends</span>
                <div className="relative flex items-center justify-center">
                  <span 
                    className="w-3 h-3 rounded-full bg-gray-400 transition-shadow duration-200"
                    style={{ 
                      boxShadow: hoveredItem === 'apex' 
                        ? '0 0 12px 4px rgba(156, 163, 175, 0.7)' 
                        : '0 0 8px 2px rgba(156, 163, 175, 0.4)'
                    }}
                  ></span>
                  <span className="absolute w-3 h-3 rounded-full bg-gray-400 animate-ping opacity-75" style={{animationDuration: '3s'}}></span>
                </div>
              </button>
              <button
                className="flex items-center justify-between px-4 py-2 text-sm text-gray-100 transition-all duration-200 relative w-full text-left"
                style={{ 
                  position: 'relative',
                  backgroundColor: hoveredItem === 'r6' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  borderLeft: '2px solid',
                  borderLeftColor: hoveredItem === 'r6' ? '#60a5fa' : 'transparent',
                  paddingLeft: '1rem'
                }}
                onClick={(e) => e.preventDefault()}
                onMouseEnter={() => setHoveredItem('r6')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span 
                  className="font-medium transition-colors duration-200"
                  style={{ color: hoveredItem === 'r6' ? '#93c5fd' : '#f3f4f6' }}
                >R6</span>
                <div className="relative flex items-center justify-center">
                  <span 
                    className="w-3 h-3 rounded-full bg-gray-400 transition-shadow duration-200"
                    style={{ 
                      boxShadow: hoveredItem === 'r6' 
                        ? '0 0 12px 4px rgba(156, 163, 175, 0.7)' 
                        : '0 0 8px 2px rgba(156, 163, 175, 0.4)'
                    }}
                  ></span>
                  <span className="absolute w-3 h-3 rounded-full bg-gray-400 animate-ping opacity-75" style={{animationDuration: '3s'}}></span>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
      
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translate(-50%, -10px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsDropdown; 